import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { EchoflicksSessionJourney } from "../types";
import { buildRepromptEvidence } from "./repromptEvidence";
import { buildRepromptActualEvidence } from "./repromptActualEvidence";
import { journeyEditChannel } from "./journey/editChannel";
import { EditChannelChip } from "./journey/EditChannelChip";

export function RepromptEvidenceSummary({
  journey,
}: {
  journey?: EchoflicksSessionJourney;
}) {
  const { plan, beats, outcomeUnknown } = buildRepromptEvidence(journey);
  const actual =
    plan && typeof plan.workload_id === "string"
      ? buildRepromptActualEvidence(
          journey?.nodes ?? [],
          plan.workload_id,
          (plan.provider_work as Record<string, unknown> | undefined)
            ?.motion_scene_ids,
        )
      : null;
  if (!plan && !beats && !outcomeUnknown) return null;
  // WRP-045: an edit asked on my.echoflicks.com says so (and whether via Felix).
  const channel = journeyEditChannel(journey?.nodes);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          Echo edit evidence
          <EditChannelChip badge={channel} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {outcomeUnknown ? (
          <p role="alert" className="text-destructive">
            Submission outcome is unknown. The edit allowance remains held;
            check the durable outbox before retrying.
          </p>
        ) : null}
        {plan ? (
          <>
            <p>
              Committed edit plan:{" "}
              <code className="break-all">{String(plan.plan_digest)}</code>
            </p>
            <p>
              Parent: {String(plan.parent_workload_id)} · root:{" "}
              {String(plan.root_workload_id)}
            </p>
            <p>
              Direct scenes: {JSON.stringify(plan.direct_scene_ids)} · dependent
              scenes: {JSON.stringify(plan.dependent_scene_ids)}
            </p>
            <p>
              Authorized provider work: {JSON.stringify(plan.provider_work)}
            </p>
            <p>
              Generation units: {JSON.stringify(plan.cost_scope)}. These are
              authorized units, not billed cost.
            </p>
            <p>
              Planned retained assets:{" "}
              {Array.isArray(plan.reuse_candidates)
                ? plan.reuse_candidates.length
                : "unknown"}
              . Actual reuse requires composer evidence.
            </p>
          </>
        ) : null}
        {actual ? (
          <>
            <p>
              Observed reuse: {actual.reusedSceneIds.length} scenes,{" "}
              {actual.reusedSpeechIndices.length} speech fragments,{" "}
              {actual.reusedMusicAssets} soundtrack assets. These are
              child-bound composer events; final asset hashes and billed cost
              require their own receipts.
            </p>
            <p>
              Observed motion attempts: {actual.motionAttemptCount}; succeeded:{" "}
              {actual.successfulMotionAttempts}; failed:{" "}
              {actual.failedMotionAttempts}. Missing attempt events do not prove
              zero provider work.
            </p>
            {actual.outsidePlanSceneIds?.length ? (
              <p role="alert" className="text-destructive">
                Motion work outside the committed plan: scenes{" "}
                {actual.outsidePlanSceneIds.join(", ")}.
              </p>
            ) : null}
          </>
        ) : null}
        <p>
          Encoded soundtrack synchronization:{" "}
          {beats ? String(beats.status) : "no matching verification receipt"}
        </p>
        {beats ? (
          <p>
            Aligned cuts: {String(beats.aligned_cut_count)} /{" "}
            {String(beats.eligible_cut_count)} eligible; excluded:{" "}
            {String(beats.excluded_cut_count)}; maximum residual:{" "}
            {String(beats.max_residual_frames)} frames.
          </p>
        ) : null}
        <details>
          <summary>Full edit and synchronization evidence</summary>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all text-xs">
            {JSON.stringify({ plan, beats, actual }, null, 2)}
          </pre>
        </details>
      </CardContent>
    </Card>
  );
}
